// Daos Ads /studiovideo — canvas compositor.
//
// Draws one frame at time `t` (seconds) onto a 2D canvas context. Layers:
//   1. Source (video element OR still image) — cover/contain against target
//   2. Effects underlay: color overlay + Ken Burns + parallax + glitch
//   3. Animated glow radial pulse (Daos brand)
//   4. Animated text block (headline/subtitle w/ gradient + signal words)
//   5. Subtitles (active segment at time t)
//   6. Brand block (logo + wordmark top-left, handle bottom-left, CTA bottom-right)
//   7. Global fade in/out
//
// The compositor is aspect/format aware (target width/height). All measurements
// are scaled from the reference 1080x1920 layout.

import { CTA_COLOR_FILLS, SIGNAL_COLORS } from "./presets";

const FONT_DISPLAY = '"Sora", "Inter", system-ui, sans-serif';
const FONT_BODY    = '"Manrope", "Inter", system-ui, sans-serif';
const BRAND = { bg0: "#050505", white: "#FFFFFF", gray300: "#D4D4D8", gray500: "#71717A" };
const GRADIENT_STOPS = [
  { stop: 0,   color: "#FF3B1F" },
  { stop: 0.5, color: "#FF8A00" },
  { stop: 1,   color: "#FFD23F" },
];

// ---------- utils ----------
function makeGradient(ctx, x0, y0, x1, y1) {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  GRADIENT_STOPS.forEach((s) => g.addColorStop(s.stop, s.color));
  return g;
}
function normWord(w) { return String(w || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ""); }
function parsePhrases(input) {
  return String(input || "").split(",").map((p) => p.split(/\s+/).map(normWord).filter(Boolean)).filter((a) => a.length);
}
function easeOutCubic(x) { return 1 - Math.pow(1 - x, 3); }
function easeInOutSine(x) { return -(Math.cos(Math.PI * x) - 1) / 2; }

// ---------- source draw ----------
function drawSourceCover(ctx, source, W, H, crop) {
  // source is HTMLVideoElement or HTMLImageElement with naturalWidth/naturalHeight or videoWidth
  const sw = source.videoWidth || source.naturalWidth || W;
  const sh = source.videoHeight || source.naturalHeight || H;
  if (!sw || !sh) return;
  ctx.fillStyle = BRAND.bg0;
  ctx.fillRect(0, 0, W, H);

  const cover = crop !== "bars";
  const scale = cover ? Math.max(W / sw, H / sh) : Math.min(W / sw, H / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = (W - dw) / 2;
  const dy = (H - dh) / 2;
  ctx.drawImage(source, dx, dy, dw, dh);
}

// Ken Burns: applies a subtle scale transform on the source. We re-draw the
// source via drawImage with an animated scale.
function drawSourceWithKenBurns(ctx, source, W, H, crop, t, total, mode) {
  const sw = source.videoWidth || source.naturalWidth || W;
  const sh = source.videoHeight || source.naturalHeight || H;
  if (!sw || !sh) { drawSourceCover(ctx, source, W, H, crop); return; }
  const p = Math.max(0, Math.min(1, t / Math.max(0.1, total)));
  let extra = 0;
  if (mode === "zoom-in")  extra = 0.14 * easeInOutSine(p);
  if (mode === "zoom-out") extra = 0.14 * (1 - easeInOutSine(p));
  const cover = crop !== "bars";
  const base = cover ? Math.max(W / sw, H / sh) : Math.min(W / sw, H / sh);
  const scale = base * (1 + extra);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = (W - dw) / 2;
  const dy = (H - dh) / 2;
  ctx.fillStyle = BRAND.bg0;
  ctx.fillRect(0, 0, W, H);
  ctx.drawImage(source, dx, dy, dw, dh);
}

// Parallax: subtle horizontal + vertical sine translation.
function drawSourceWithParallax(ctx, source, W, H, crop, t) {
  const sw = source.naturalWidth || W;
  const sh = source.naturalHeight || H;
  if (!sw || !sh) return;
  const base = Math.max(W / sw, H / sh) * 1.06;
  const dw = sw * base;
  const dh = sh * base;
  const px = Math.sin(t * 0.6) * (dw - W) * 0.08;
  const py = Math.cos(t * 0.5) * (dh - H) * 0.06;
  const dx = (W - dw) / 2 + px;
  const dy = (H - dh) / 2 + py;
  ctx.fillStyle = BRAND.bg0;
  ctx.fillRect(0, 0, W, H);
  ctx.drawImage(source, dx, dy, dw, dh);
}

// Glitch: RGB channel offset + occasional slice shift.
function applyGlitch(ctx, W, H, intensity, t) {
  if (intensity <= 0) return;
  const jitter = Math.sin(t * 30) * 0.5 + 0.5; // 0..1 fast
  const off = Math.round(intensity * 6 * (0.4 + jitter * 0.6));
  const src = ctx.getImageData(0, 0, W, H);
  const data = src.data;
  // R channel offset right
  const shifted = new Uint8ClampedArray(data);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W - off; x++) {
      const i = (y * W + x) * 4;
      shifted[i] = data[i + off * 4]; // R shifted
    }
    for (let x = off; x < W; x++) {
      const i = (y * W + x) * 4;
      shifted[i + 2] = data[i - off * 4 + 2]; // B shifted the other way
    }
  }
  ctx.putImageData(new ImageData(shifted, W, H), 0, 0);

  // Occasional slice shift (every ~0.2s)
  if (Math.random() < intensity * 0.4) {
    const sliceH = Math.round(6 + Math.random() * 30);
    const sy = Math.floor(Math.random() * (H - sliceH));
    const dx = Math.round((Math.random() - 0.5) * 40 * intensity);
    const slice = ctx.getImageData(0, sy, W, sliceH);
    ctx.putImageData(slice, dx, sy);
  }
}

// ---------- glow pulse ----------
function drawGlowPulse(ctx, W, H, t, enabled) {
  if (!enabled) return;
  const puls = 0.65 + 0.35 * (0.5 + 0.5 * Math.sin(t * 0.6 * Math.PI * 2));
  const r1 = ctx.createRadialGradient(W * 0.85, H * 0.05, 0, W * 0.85, H * 0.05, Math.max(W, H) * 0.7);
  r1.addColorStop(0, `rgba(255,138,0,${0.16 * puls})`);
  r1.addColorStop(0.6, "rgba(255,138,0,0)");
  ctx.fillStyle = r1;
  ctx.fillRect(0, 0, W, H);

  const r2 = ctx.createRadialGradient(W * 0.1, H * 0.0, 0, W * 0.1, H * 0.0, Math.max(W, H) * 0.55);
  r2.addColorStop(0, `rgba(255,59,31,${0.12 * puls})`);
  r2.addColorStop(0.6, "rgba(255,59,31,0)");
  ctx.fillStyle = r2;
  ctx.fillRect(0, 0, W, H);
}

// ---------- overlay ----------
function drawOverlay(ctx, W, H, color) {
  if (!color) return;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, W, H);
}

// ---------- animated text layer ----------
function textVisibility(t, startAt, duration) {
  if (t < startAt) return { alpha: 0, progress: 0, phase: "before" };
  const end = startAt + duration;
  if (t > end) return { alpha: 0, progress: 1, phase: "after" };
  const inDur = 0.4;
  const outDur = 0.4;
  const inEnd = startAt + inDur;
  const outStart = end - outDur;
  // Floor of 0.05 during the in-phase so text is visible at the exact starting
  // frame (t == startAt); otherwise easeOutCubic(0) = 0 would leave the frame blank.
  if (t < inEnd)  return { alpha: Math.max(0.05, easeOutCubic((t - startAt) / inDur)), progress: (t - startAt) / duration, phase: "in" };
  if (t > outStart) return { alpha: 1 - easeOutCubic((t - outStart) / outDur), progress: (t - startAt) / duration, phase: "out" };
  return { alpha: 1, progress: (t - startAt) / duration, phase: "hold" };
}

function drawTextLayer(ctx, W, H, textCfg, t) {
  if (!textCfg?.enabled) return;
  const vis = textVisibility(t, textCfg.startAt || 0, textCfg.duration || 3);
  if (vis.alpha <= 0.001) return;

  const scale = W / 1080;
  const padX = Math.round(80 * scale);
  const maxW = W - padX * 2;
  const boxCenterY =
    textCfg.position === "top"    ? H * 0.22 :
    textCfg.position === "center" ? H * 0.5  :
                                    H * 0.75;

  // Entry translation
  let tx = 0, ty = 0;
  if (vis.phase === "in") {
    const k = 1 - easeOutCubic((t - textCfg.startAt) / 0.4);
    if (textCfg.animation === "slide-up")   ty = k * Math.round(60 * scale);
    if (textCfg.animation === "slide-left") tx = -k * Math.round(80 * scale);
    if (textCfg.animation === "bounce")     ty = k * Math.round(Math.sin(k * Math.PI) * 40 * scale);
  }

  ctx.save();
  ctx.globalAlpha = vis.alpha;
  ctx.translate(tx, ty);

  // Headline styled with gradient/signal words
  const gradPhrases = parsePhrases(textCfg.gradientWords);
  const signalPhrases = parsePhrases(textCfg.signalWords);
  const signalHex = SIGNAL_COLORS[textCfg.signalColor] || null;

  const headlineSize = fitTextSize(ctx, textCfg.headline, maxW, {
    startSize: Math.round(96 * scale),
    minSize: Math.round(36 * scale),
    weight: 800,
    family: FONT_DISPLAY,
    maxLines: 4,
  });
  ctx.font = `800 ${headlineSize}px ${FONT_DISPLAY}`;
  const hLines = wrapLines(ctx, textCfg.headline, maxW);
  const lineHeight = Math.round(headlineSize * 1.05);
  const totalH = hLines.length * lineHeight;
  const startY = boxCenterY - totalH / 2;

  // Backdrop (semi-transparent) for legibility on any video
  ctx.save();
  ctx.fillStyle = "rgba(5,5,5,0.42)";
  const bdPadX = Math.round(28 * scale);
  const bdPadY = Math.round(20 * scale);
  const maxLineWidth = hLines.reduce((m, l) => Math.max(m, ctx.measureText(l).width), 0);
  const bdW = Math.min(maxW, maxLineWidth + bdPadX * 2);
  const bdH = totalH + bdPadY * 2;
  const bdX = (W - bdW) / 2;
  const bdY = startY - bdPadY;
  roundedRect(ctx, bdX, bdY, bdW, bdH, Math.round(18 * scale));
  ctx.fill();
  ctx.restore();

  // Draw styled headline word-by-word
  hLines.forEach((line, i) => {
    drawStyledLine(ctx, line, {
      x: W / 2, y: startY + i * lineHeight + Math.round(headlineSize * 0.82),
      align: "center", size: headlineSize,
      gradPhrases, signalPhrases, signalHex,
    });
  });

  // Subtitle if any
  if (textCfg.subtitle && textCfg.subtitle.trim()) {
    const subSize = Math.round(38 * scale);
    ctx.font = `400 ${subSize}px ${FONT_BODY}`;
    const subLines = wrapLines(ctx, textCfg.subtitle, maxW);
    subLines.forEach((line, i) => {
      ctx.fillStyle = BRAND.gray300;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(line, W / 2, startY + totalH + Math.round(32 * scale) + i * Math.round(subSize * 1.32) + Math.round(subSize * 0.82));
    });
  }

  ctx.restore();
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function wrapLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let cur = words[0];
  for (let i = 1; i < words.length; i++) {
    const probe = cur + " " + words[i];
    if (ctx.measureText(probe).width > maxWidth) { lines.push(cur); cur = words[i]; }
    else cur = probe;
  }
  lines.push(cur);
  return lines;
}

function fitTextSize(ctx, text, maxWidth, { startSize, minSize, weight, family, maxLines }) {
  let s = startSize;
  for (; s >= minSize; s -= 2) {
    ctx.font = `${weight} ${s}px ${family}`;
    const lines = wrapLines(ctx, text, maxWidth);
    if (lines.length <= maxLines) return s;
  }
  return minSize;
}

function drawStyledLine(ctx, line, { x, y, align, size, gradPhrases, signalPhrases, signalHex }) {
  const tokens = String(line).split(/\s+/).filter(Boolean).map((w) => ({ text: w, style: "normal" }));
  // Match phrases
  const applyMatches = (phrases, style) => {
    for (const ph of phrases) {
      for (let i = 0; i <= tokens.length - ph.length; i++) {
        let ok = true;
        for (let j = 0; j < ph.length; j++) {
          if (normWord(tokens[i + j].text) !== ph[j]) { ok = false; break; }
        }
        if (ok) for (let j = 0; j < ph.length; j++) tokens[i + j].style = style;
      }
    }
  };
  applyMatches(gradPhrases, "gradient");
  if (signalHex) applyMatches(signalPhrases, "signal");

  const spaceW = ctx.measureText(" ").width;
  const lineWidth = tokens.reduce((acc, t, i) => acc + (i > 0 ? spaceW : 0) + ctx.measureText(t.text).width, 0);
  let cursor = x;
  if (align === "center") cursor = x - lineWidth / 2;
  if (align === "right")  cursor = x - lineWidth;

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  tokens.forEach((tok, i) => {
    if (i > 0) cursor += spaceW;
    const w = ctx.measureText(tok.text).width;
    if (tok.style === "gradient") {
      ctx.fillStyle = makeGradient(ctx, cursor, y - size, cursor + w, y);
    } else if (tok.style === "signal") {
      ctx.fillStyle = signalHex;
    } else {
      ctx.fillStyle = BRAND.white;
    }
    ctx.fillText(tok.text, cursor, y);
    cursor += w;
  });
}

// ---------- subtitles ----------
function drawSubtitles(ctx, W, H, subCfg, t) {
  if (!subCfg?.enabled) return;
  const items = subCfg.items || [];
  const active = items.find((s) => t >= s.start && t <= s.end);
  if (!active) return;
  const scale = W / 1080;
  const size = Math.round(46 * scale);
  ctx.font = `700 ${size}px ${FONT_DISPLAY}`;
  const maxW = W - Math.round(120 * scale);
  const lines = wrapLines(ctx, active.text || "", maxW);
  const lineH = Math.round(size * 1.15);
  const totalH = lines.length * lineH;
  const yStart = H - Math.round(220 * scale) - totalH;

  if (subCfg.style === "backdrop") {
    ctx.fillStyle = "rgba(5,5,5,0.8)";
    const pad = Math.round(22 * scale);
    const maxLineWidth = lines.reduce((m, l) => Math.max(m, ctx.measureText(l).width), 0);
    const bw = Math.min(maxW, maxLineWidth + pad * 2);
    roundedRect(ctx, (W - bw) / 2, yStart - pad, bw, totalH + pad * 2, Math.round(16 * scale));
    ctx.fill();
  } else {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.85)";
    ctx.shadowBlur = Math.round(10 * scale);
    ctx.shadowOffsetY = Math.round(2 * scale);
  }
  ctx.fillStyle = BRAND.white;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, yStart + i * lineH + Math.round(size * 0.82));
  });
  if (subCfg.style !== "backdrop") ctx.restore();
}

// ---------- brand layer ----------
function drawBrand(ctx, W, H, brandCfg, logo) {
  if (!brandCfg?.enabled) return;
  const scale = W / 1080;
  const pad = Math.round(48 * scale);

  // Logo + wordmark top-left
  const logoH = Math.round(72 * scale);
  let logoW = logoH;
  if (logo && logo.complete && logo.naturalWidth > 0) {
    logoW = Math.round(logoH * (logo.naturalWidth / logo.naturalHeight));
    ctx.drawImage(logo, pad, pad, logoW, logoH);
  } else {
    ctx.fillStyle = makeGradient(ctx, pad, pad, pad + logoH, pad + logoH);
    ctx.beginPath(); ctx.arc(pad + logoH / 2, pad + logoH / 2, logoH / 2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = BRAND.bg0;
    ctx.font = `800 ${Math.round(logoH * 0.55)}px ${FONT_DISPLAY}`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("D", pad + logoH / 2, pad + logoH / 2 + 2);
    logoW = logoH;
  }
  ctx.font = `800 ${Math.round(34 * scale)}px ${FONT_DISPLAY}`;
  ctx.fillStyle = BRAND.white;
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillText("Daos Ads", pad + logoW + Math.round(18 * scale), pad + Math.round(logoH * 0.68));

  // Handle bottom-left
  ctx.font = `600 ${Math.round(24 * scale)}px ${FONT_DISPLAY}`;
  ctx.fillStyle = BRAND.gray500;
  ctx.fillText(brandCfg.handle || "@DAOS.ADS", pad, H - pad);

  // CTA pill bottom-right
  const cta = String(brandCfg.cta || "").trim();
  if (!cta) return;
  const label = cta.toUpperCase() + (cta.endsWith("→") ? "" : " →");
  let fontSize = Math.round(26 * scale);
  ctx.font = `700 ${fontSize}px ${FONT_DISPLAY}`;
  const tw = ctx.measureText(label).width;
  const padX = Math.round(fontSize * 1.3);
  const padY = Math.round(fontSize * 0.7);
  const bw = tw + padX * 2;
  const bh = fontSize + padY * 2;
  const bx = W - pad - bw;
  const by = H - pad - bh + Math.round(fontSize * 0.4);
  const radius = bh / 2;

  const color = brandCfg.ctaColor || "gradient";
  if (color === "gradient" || !CTA_COLOR_FILLS[color]) {
    ctx.fillStyle = makeGradient(ctx, bx, by, bx + bw, by);
  } else {
    ctx.fillStyle = CTA_COLOR_FILLS[color];
  }
  ctx.beginPath();
  ctx.moveTo(bx + radius, by);
  ctx.lineTo(bx + bw - radius, by);
  ctx.arc(bx + bw - radius, by + radius, radius, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(bx + radius, by + bh);
  ctx.arc(bx + radius, by + radius, radius, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = color === "white" ? BRAND.bg0 : "#0a0a0a";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(label, bx + bw / 2, by + bh / 2 + 2);
}

// ---------- global fade ----------
function applyGlobalFade(ctx, W, H, t, total, fadeIn, fadeOut) {
  if (fadeIn > 0 && t < fadeIn) {
    ctx.fillStyle = `rgba(0,0,0,${1 - t / fadeIn})`;
    ctx.fillRect(0, 0, W, H);
  }
  const outStart = total - fadeOut;
  if (fadeOut > 0 && t > outStart) {
    const p = Math.min(1, (t - outStart) / fadeOut);
    ctx.fillStyle = `rgba(0,0,0,${p})`;
    ctx.fillRect(0, 0, W, H);
  }
}

// ---------- OVERLAY_COLORS map ----------
const OVERLAY_COLORS = {
  none: null, dark: "rgba(5,5,5,0.3)", orange: "rgba(255,138,0,0.15)", red: "rgba(255,59,31,0.15)",
};

// ---------- main renderFrame ----------
// source: { type: "video"|"image", el: HTMLVideoElement|HTMLImageElement }
// layers: from defaultLayers()
// state: { W, H, total, t, logo }
export function renderFrame(ctx, source, layers, state) {
  const { W, H, total, t, logo } = state;

  // Source frame
  if (layers.effects?.kenBurns && layers.effects.kenBurns !== "none") {
    drawSourceWithKenBurns(ctx, source.el, W, H, layers.crop, t, total, layers.effects.kenBurns);
  } else if (source.type === "image" && layers.effects?.parallax) {
    drawSourceWithParallax(ctx, source.el, W, H, layers.crop, t);
  } else {
    drawSourceCover(ctx, source.el, W, H, layers.crop);
  }

  // Glitch (destructive; do before overlays)
  const glitchVal = { none: 0, low: 0.15, mid: 0.35, high: 0.6 }[layers.effects?.glitch || "none"];
  if (glitchVal > 0) applyGlitch(ctx, W, H, glitchVal, t);

  // Overlay color
  drawOverlay(ctx, W, H, OVERLAY_COLORS[layers.effects?.overlay || "none"]);

  // Glow pulse (Daos)
  drawGlowPulse(ctx, W, H, t, !!layers.effects?.glowPulse);

  // Animated text
  drawTextLayer(ctx, W, H, layers.text, t);

  // Subtitles
  drawSubtitles(ctx, W, H, layers.subtitles, t);

  // Brand
  drawBrand(ctx, W, H, layers.brand, logo);

  // Global fade in/out
  applyGlobalFade(ctx, W, H, t, total, layers.effects?.fadeIn || 0, layers.effects?.fadeOut || 0);
}

// ---------- image loader ----------
export function loadImageEl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
